import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { Select, SelectProps } from "antd";

import { placesFetchRequest } from "../../../store/place_selector/actions";
import { Place } from "../../../store/place_selector/types";

const { Option } = Select;

export interface PlaceSelectorProps extends SelectProps<string> {
  value?: string;
}

export const PlaceSelector: React.FC<PlaceSelectorProps> = (props) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  const [state, setState] = useState(() => {
    const value = props.value ?? "";
    if (value) {
      dispatch(
        placesFetchRequest({
          search: value,
          locale: i18n.language,
          onSearchResult: (places) => setState((currentState) => ({ ...currentState, isLoading: false, places })),
        })
      );
    }

    return {
      value,
      search: "",
      places: [] as Place[],
      isLoading: !!value,
    };
  });

  const timeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (state.search) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      timeoutId.current = setTimeout(() => {
        setState((currentState) => ({ ...currentState, isLoading: true }));
        dispatch(
          placesFetchRequest({
            search: state.search,
            locale: i18n.language,
            onSearchResult: (places) => setState((currentState) => ({ ...currentState, isLoading: false, places })),
          })
        );
        if (timeoutId.current) {
          clearTimeout(timeoutId.current);
        }
      }, 300);
    }
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [state.search, i18n.language, dispatch]);

  const handleSearch = (value: string) => setState((currentState) => ({ ...currentState, search: value }));

  const handleChange = (value: string) => {
    setState((currentState) => ({ ...currentState, value }));
  };

  return (
    <Select
      showSearch
      value={state.value}
      onSearch={handleSearch}
      loading={state.isLoading}
      filterOption={false}
      onChange={handleChange}
      notFoundContent={false}
      {...props}
      showArrow={state.isLoading || props.showArrow}
    >
      {state.places.map((place) => (
        <Option key={`place-${place.type}-${place.code}`} value={place.code}>
          {place.name} ({place.code})
        </Option>
      ))}
    </Select>
  );
};
